/**
 * CreateTagPage Component
 * Full page for creating a new tag with optional move assignments
 *
 * Features:
 * - Form validation (name required, max 100 chars)
 * - Color picker with 8 presets
 * - Move assignment with search
 * - Series section (disabled/grayed out)
 * - Success → navigate to /tags archive
 * - Cancel → navigate back to /tags
 */

import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useConfirmationModals } from '@/hooks/useConfirmationModals'
import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/tags/ColorPicker'
import { ConfirmationModal } from '@/components/shared/ConfirmationModal'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import {
  AddItemsDrawer,
  type DrawerItem,
} from '@/components/shared/AddItemsDrawer'
import { MoveListItem } from '@/components/shared/MoveListItem'
import { InlineEmptyState } from '@/components/shared/InlineEmptyState'
import { useCreateTag } from '@/hooks/useCreateTag'
import { useMoves } from '@/hooks/useMoves'
import { useSeries } from '@/hooks/useSeriesList'
import { useAddTagsToEntity } from '@/hooks/useTaggings'

export function CreateTagPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState<string | undefined>(undefined)
  const [selectedMoveIds, setSelectedMoveIds] = useState<Set<string>>(new Set())
  const [selectedSeriesIds, setSelectedSeriesIds] = useState<Set<string>>(
    new Set()
  )

  // UI state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSeriesDrawerOpen, setIsSeriesDrawerOpen] = useState(false)
  const [nameError, setNameError] = useState('')
  const [moveToRemove, setMoveToRemove] = useState<string | null>(null)
  const modals = useConfirmationModals()

  // API hooks
  const createTagMutation = useCreateTag()
  const addTagsToEntityMutation = useAddTagsToEntity()
  // Fetch available moves and series
  const { data: movesData, isLoading: movesLoading } = useMoves({ limit: 100 })
  const { data: seriesData, isLoading: seriesLoading } = useSeries({
    limit: 100,
  })

  // Get moves and series data
  const allMoves = useMemo(() => movesData?.moves || [], [movesData])
  const allSeries = useMemo(() => seriesData?.series || [], [seriesData])

  /**
   * Validate name field
   */
  const validateName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError('Tag name is required')
      return false
    }
    if (value.length > 100) {
      setNameError('Tag name cannot exceed 100 characters')
      return false
    }
    setNameError('')
    return true
  }

  /**
   * Handle name input change with validation
   */
  const handleNameChange = (value: string) => {
    setName(value)
    if (nameError) {
      validateName(value)
    }
  }

  /**
   * Handle adding moves from drawer
   */
  const handleAddMoves = (moveIds: string[]) => {
    setSelectedMoveIds(new Set(moveIds))
  }

  /**
   * Handle removing a single move
   */
  const handleRemoveMove = (moveId: string) => {
    setSelectedMoveIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(moveId)
      return newSet
    })
    setMoveToRemove(null)
  }

  /**
   * Get selected moves with full details
   */
  const selectedMoves = useMemo(() => {
    return allMoves.filter(move => selectedMoveIds.has(move.id))
  }, [allMoves, selectedMoveIds])

  /**
   * Get selected series with full details
   */
  const selectedSeries = useMemo(() => {
    return allSeries.filter(series => selectedSeriesIds.has(series.id))
  }, [allSeries, selectedSeriesIds])

  /**
   * Handle adding series from drawer
   */
  const handleAddSeries = (seriesIds: string[]) => {
    setSelectedSeriesIds(new Set(seriesIds))
  }

  /**
   * Handle removing a single series
   */
  const handleRemoveSeries = (seriesId: string) => {
    setSelectedSeriesIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(seriesId)
      return newSet
    })
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate name
    if (!validateName(name)) {
      return
    }

    // Create tag via API
    createTagMutation.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        color,
      },
      {
        onSuccess: async createdTag => {
          // After tag is created, add taggings for selected moves and series
          try {
            const taggingPromises: Promise<unknown>[] = []

            // Add tag to all selected moves
            if (selectedMoveIds.size > 0) {
              for (const moveId of selectedMoveIds) {
                taggingPromises.push(
                  addTagsToEntityMutation.mutateAsync({
                    entityType: 'move',
                    entityId: moveId,
                    data: { tagIds: [createdTag.id] },
                  })
                )
              }
            }

            // Add tag to all selected series
            if (selectedSeriesIds.size > 0) {
              for (const seriesId of selectedSeriesIds) {
                taggingPromises.push(
                  addTagsToEntityMutation.mutateAsync({
                    entityType: 'series',
                    entityId: seriesId,
                    data: { tagIds: [createdTag.id] },
                  })
                )
              }
            }

            // Wait for all taggings to be created
            await Promise.all(taggingPromises)

            toast({
              title: 'Tag created',
              description: `"${name.trim()}" has been created successfully${
                taggingPromises.length > 0
                  ? ` and applied to ${selectedMoveIds.size} move${selectedMoveIds.size !== 1 ? 's' : ''} and ${selectedSeriesIds.size} series`
                  : ''
              }.`,
            })
            navigate('/tags')
          } catch (error) {
            // Tag was created but taggings failed
            toast({
              title: 'Tag created with warnings',
              description: `"${name.trim()}" was created, but some assignments failed. You can add them later.`,
              variant: 'destructive',
            })
            navigate('/tags')
          }
        },
        onError: error => {
          toast({
            title: 'Failed to create tag',
            description:
              error instanceof Error ? error.message : 'An error occurred',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        {(movesLoading || seriesLoading) && <LoadingSpinner size="lg" />}

        {!movesLoading && !seriesLoading && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tag Info Section */}
            <section className="space-y-6 rounded-2xl border bg-white p-6">
              {/* Header and Action Buttons */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-black">
                  Tag Information
                </h2>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/tags')}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={createTagMutation.isPending || !name.trim()}
                    className="bg-[#0033A0] hover:bg-[#002080]"
                  >
                    {createTagMutation.isPending ? 'Creating...' : 'Create Tag'}
                  </Button>
                </div>
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <label
                  htmlFor="tag-name"
                  className="block text-sm font-medium text-gray-900"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="tag-name"
                  type="text"
                  value={name}
                  onChange={e => handleNameChange(e.target.value)}
                  onBlur={() => validateName(name)}
                  maxLength={100}
                  className={cn(
                    'w-full rounded-lg border px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]',
                    nameError ? 'border-red-500' : 'border-gray-300'
                  )}
                  placeholder="e.g., Fundamentals, Submissions, Guard Passing"
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? 'name-error' : undefined}
                  disabled={createTagMutation.isPending}
                />
                {nameError && (
                  <p id="name-error" className="text-sm text-red-500">
                    {nameError}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {name.length}/100 characters
                </p>
              </div>

              {/* Description Field */}
              <div className="space-y-2">
                <label
                  htmlFor="tag-description"
                  className="block text-sm font-medium text-gray-900"
                >
                  Description <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  id="tag-description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  className={cn(
                    'w-full resize-none rounded-lg border border-gray-300 px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]'
                  )}
                  placeholder="Optional: Add details about what this tag represents"
                  disabled={createTagMutation.isPending}
                />
              </div>

              {/* Color Picker */}
              <ColorPicker
                value={color}
                onChange={setColor}
                label="Tag Color"
                showNoColor={true}
              />
            </section>

            {/* Assign to Moves Section */}
            <section className="space-y-4 rounded-2xl border bg-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-black">
                    Assign to Moves
                    <span className="ml-2 text-gray-400">(Optional)</span>
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Select moves to apply this tag to
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDrawerOpen(true)}
                  className={cn(
                    'rounded-lg bg-[#0033A0] px-4 py-2 text-sm font-medium text-white',
                    'transition-colors hover:bg-[#002B87]',
                    'focus:outline-none focus:ring-2 focus:ring-[#0033A0] focus:ring-offset-2'
                  )}
                  disabled={createTagMutation.isPending}
                >
                  <Plus className="mr-2 inline-block h-4 w-4" />
                  Add Moves
                </button>
              </div>

              {/* Selected Moves List */}
              {selectedMoveIds.size === 0 ? (
                <InlineEmptyState
                  message="No moves selected yet"
                  secondaryMessage='Click "Add Moves" to assign moves to this tag'
                  className="py-8"
                />
              ) : (
                <div className="space-y-3">
                  {selectedMoves.map(move => (
                    <MoveListItem
                      key={move.id}
                      id={move.id}
                      name={move.name}
                      description={move.description}
                      showThumbnail={false}
                      onRemove={() => {
                        setMoveToRemove(move.id)
                        modals.openModal('removeMove')
                      }}
                      className="p-4"
                    />
                  ))}
                  <div className="flex items-center justify-between border-t pt-3">
                    <p className="text-sm text-gray-700">
                      {selectedMoveIds.size} move
                      {selectedMoveIds.size !== 1 ? 's' : ''} selected
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedMoveIds(new Set())}
                      className="text-sm text-[#0033A0] hover:underline"
                      disabled={createTagMutation.isPending}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Assign to Series Section */}
            <section className="space-y-4 rounded-2xl border bg-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-black">
                    Assign to Series
                    <span className="ml-2 text-gray-400">(Optional)</span>
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Select series to apply this tag to
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSeriesDrawerOpen(true)}
                  className={cn(
                    'rounded-lg bg-[#0033A0] px-4 py-2 text-sm font-medium text-white',
                    'transition-colors hover:bg-[#002B87]',
                    'focus:outline-none focus:ring-2 focus:ring-[#0033A0] focus:ring-offset-2'
                  )}
                  disabled={createTagMutation.isPending}
                >
                  <Plus className="mr-2 inline-block h-4 w-4" />
                  Add Series
                </button>
              </div>

              {/* Selected Series List */}
              {selectedSeriesIds.size === 0 ? (
                <InlineEmptyState
                  message="No series selected yet"
                  secondaryMessage='Click "Add Series" to assign series to this tag'
                  className="py-8"
                />
              ) : (
                <div className="space-y-3">
                  {selectedSeries.map(series => (
                    <div
                      key={series.id}
                      className="flex items-start gap-3 rounded-lg border p-4"
                    >
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {series.name}
                        </h4>
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                          {series.description}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSeries(series.id)}
                        className="text-[#D62828] hover:bg-[#D62828]/10"
                        aria-label={`Remove ${series.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t pt-3">
                    <p className="text-sm text-gray-700">
                      {selectedSeriesIds.size} series
                      {selectedSeriesIds.size !== 1 ? '' : ''} selected
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedSeriesIds(new Set())}
                      className="text-sm text-[#0033A0] hover:underline"
                      disabled={createTagMutation.isPending}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </section>
          </form>
        )}
      </main>

      {/* Remove Move Confirmation */}
      <ConfirmationModal
        {...modals.getModalProps(
          'removeMove',
          () => moveToRemove && handleRemoveMove(moveToRemove),
          {
            title: 'Remove Move?',
            description: `Are you sure you want to remove "${selectedMoves.find(m => m.id === moveToRemove)?.name}" from this tag?`,
            confirmText: 'Remove',
            confirmVariant: 'destructive',
          }
        )}
        onClose={() => {
          modals.closeModal()
          setMoveToRemove(null)
        }}
      />

      {/* Add Moves Drawer */}
      <AddItemsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Moves to Tag"
        items={allMoves as DrawerItem[]}
        currentItemIds={Array.from(selectedMoveIds)}
        onAddItems={handleAddMoves}
        searchPlaceholder="Search moves..."
        addButtonLabel="Add Selected Moves"
        emptyMessage="No moves available"
        emptySearchMessage="No moves match your search"
      />

      {/* Add Series Drawer */}
      <AddItemsDrawer
        isOpen={isSeriesDrawerOpen}
        onClose={() => setIsSeriesDrawerOpen(false)}
        title="Add Series to Tag"
        items={allSeries.map(
          (series): DrawerItem => ({
            id: series.id,
            name: series.name,
            description: series.description || '',
          })
        )}
        currentItemIds={Array.from(selectedSeriesIds)}
        onAddItems={handleAddSeries}
        searchPlaceholder="Search series..."
        addButtonLabel="Add Selected Series"
        emptyMessage="No series available"
        emptySearchMessage="No series match your search"
      />
    </div>
  )
}
